import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { unwrapNewApiEnvelope } from 'src/app/shared/utils/new-api-envelope.util';
import { DataService } from 'src/app/services/data.service';
import {
  AudienceBuilding,
  AudienceFacets,
  AudiencePreviewResult,
  CampaignDisplayStatus,
  CampaignImageUploadResult,
  InAppCampaignAudience,
  InAppCampaignDetail,
  InAppCampaignDraftPayload,
  InAppCampaignFormPayload,
  InAppCampaignListItem,
  InAppCampaignModal,
  InAppCampaignMutationResult,
  InAppCampaignPublishResult,
  InAppCampaignSummary,
  Paginate,
} from 'src/app/interface/in-app-campaign.interface';

/**
 * Admin-facing slice of the in-app campaigns API (VDS-3389, PR3).
 *
 * Precedent: `tickets-api.service.ts:94` — absolute URL against `environment.newApiBackend`,
 * NOT `notice-munily.service.ts` (that one targets the legacy backend). `environment.apiUrlV2`
 * does not exist; using it would compile and 404 at runtime as `undefined/...`.
 */
@Injectable({ providedIn: 'root' })
export class InAppCampaignService extends DataService {
  private readonly _base = `${environment.newApiBackend}/api/v2/in-app-campaigns`;

  constructor(
    private readonly _http: HttpClient,
    protected injector: Injector,
    protected store: Store<unknown>,
  ) {
    super(injector, store);
  }

  /**
   * `GET /admin/next` always answers `200`; a global interceptor wraps every payload as
   * `{ success, data, ... }` and the empty-result contract is `data: null` — never a `204`.
   * The caller MUST branch on `data === null`, never on the HTTP status code. Any network/5xx
   * failure is swallowed here so a marketing modal never breaks entry to the dashboard.
   */
  getNext(): Observable<InAppCampaignModal | null> {
    return this._http.get<unknown>(`${this._base}/admin/next`).pipe(
      map((body) => unwrapNewApiEnvelope<InAppCampaignModal | null>(body) ?? null),
      catchError(() => of(null)),
    );
  }

  /** Fire-and-forget (MOD-5): callers must not await this — errors are swallowed here too. */
  registerClick(id: string): Observable<void> {
    return this._http.post<unknown>(`${this._base}/admin/${id}/click`, {}).pipe(
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  /** Fire-and-forget (MOD-5): callers must not await this — errors are swallowed here too. */
  registerDismiss(id: string): Observable<void> {
    return this._http.post<unknown>(`${this._base}/admin/${id}/dismiss`, {}).pipe(
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  /**
   * Super-admin console list (PR4a, CON-2). Unlike the admin-facing methods above, errors are
   * NOT swallowed here — the console must know when a fetch fails so it can show its own state.
   * `displayStatus` is omitted entirely for the `'all'` filter chip.
   */
  list(params: {
    page: number;
    rowsPerPage: number;
    displayStatus?: CampaignDisplayStatus;
  }): Observable<Paginate<InAppCampaignListItem>> {
    // The wire param is `limit`, NOT `rowsPerPage`: that is what the backend query DTO declares,
    // and the global pipe runs `forbidNonWhitelisted`, so an undeclared key is a 400 on every
    // request. `rowsPerPage` stays as the caller-facing name because that is the UI's own concept.
    let httpParams = new HttpParams().set('page', params.page).set('limit', params.rowsPerPage);
    if (params.displayStatus) {
      httpParams = httpParams.set('displayStatus', params.displayStatus);
    }
    return this._http
      .get<unknown>(`${this._base}/super-admin`, { params: httpParams })
      .pipe(map((body) => unwrapNewApiEnvelope<Paginate<InAppCampaignListItem>>(body)));
  }

  /** `GET /super-admin/summary` — the 4 console metric cards, all computed server-side. */
  summary(): Observable<InAppCampaignSummary> {
    return this._http
      .get<unknown>(`${this._base}/super-admin/summary`)
      .pipe(map((body) => unwrapNewApiEnvelope<InAppCampaignSummary>(body)));
  }

  /** `POST /super-admin/:id/finish` — RN-2. Not idempotent-tolerant client-side; the row menu
   * disables this action via server-supplied `canFinish` before the user can even try. */
  finish(id: string): Observable<void> {
    return this._http.post<unknown>(`${this._base}/super-admin/${id}/finish`, {}).pipe(map(() => undefined));
  }

  /**
   * `POST /super-admin/media` (multipart, PR4b-1/RN-4). Errors are NOT swallowed — the media
   * section renders a server `422` into its own `imageError` slot, never a toast (MED-3, MED-4).
   * `create`/`update`/`publish`/`previewAudience`/`listAudienceBuildings` remain PR4b-2 scope.
   */
  uploadImage(file: File): Observable<CampaignImageUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this._http
      .post<unknown>(`${this._base}/super-admin/media`, formData)
      .pipe(map((body) => unwrapNewApiEnvelope<CampaignImageUploadResult>(body)));
  }

  /**
   * `GET /super-admin/:id` — the full editable shape the console form reopens a campaign with.
   * Errors are NOT swallowed (same rule as `list()`): the form has to know the load failed so it
   * can toast and go back, rather than silently present an empty "new campaign".
   */
  getById(id: string): Observable<InAppCampaignDetail> {
    return this._http
      .get<unknown>(`${this._base}/super-admin/${id}`)
      .pipe(map((res) => unwrapNewApiEnvelope<InAppCampaignDetail>(res)));
  }

  /** `POST /super-admin` — draft creation (PR4b-2, task 6.12). A draft body may be partial. */
  create(body: InAppCampaignFormPayload | InAppCampaignDraftPayload): Observable<InAppCampaignMutationResult> {
    return this._http
      .post<unknown>(`${this._base}/super-admin`, body)
      .pipe(map((res) => unwrapNewApiEnvelope<InAppCampaignMutationResult>(res)));
  }

  /** `PATCH /super-admin/:id` — draft/published edits (AUTH-4 editability rules are server-side). */
  update(id: string, body: InAppCampaignFormPayload | InAppCampaignDraftPayload): Observable<InAppCampaignMutationResult> {
    return this._http
      .patch<unknown>(`${this._base}/super-admin/${id}`, body)
      .pipe(map((res) => unwrapNewApiEnvelope<InAppCampaignMutationResult>(res)));
  }

  /** `POST /super-admin/:id/publish` — completeness gate (422) lives server-side. */
  publish(id: string): Observable<InAppCampaignPublishResult> {
    return this._http
      .post<unknown>(`${this._base}/super-admin/${id}/publish`, {})
      .pipe(map((res) => unwrapNewApiEnvelope<InAppCampaignPublishResult>(res)));
  }

  /**
   * `GET /super-admin/audience/preview` (AUD-5) — building count only, never persists anything.
   * Errors are NOT swallowed: the reach banner has its own loading/error affordance (task 6.5).
   */
  previewAudience(audience: InAppCampaignAudience): Observable<AudiencePreviewResult> {
    return this._http
      .get<unknown>(`${this._base}/super-admin/audience/preview`, { params: this._audienceParams(audience) })
      .pipe(map((res) => unwrapNewApiEnvelope<AudiencePreviewResult>(res)));
  }

  /**
   * `GET /super-admin/audience/facets` — the country/company values that active buildings actually
   * have, each with its building count. Replaces deriving the chips from whichever page of
   * `audience/buildings` happened to be loaded, which made the country row a sample of at most 100
   * buildings and left the chips countless.
   */
  audienceFacets(): Observable<AudienceFacets> {
    return this._http
      .get<unknown>(`${this._base}/super-admin/audience/facets`)
      .pipe(map((res) => unwrapNewApiEnvelope<AudienceFacets>(res)));
  }

  /** `GET /super-admin/audience/buildings` (AUD-6) — `{id,name,country,company,units}` rows. */
  listAudienceBuildings(params: { search?: string; page: number; rowsPerPage: number }): Observable<Paginate<AudienceBuilding>> {
    // See `list()` — the wire param is `limit`; sending `rowsPerPage` is a 400.
    let httpParams = new HttpParams().set('page', params.page).set('limit', params.rowsPerPage);
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    return this._http
      .get<unknown>(`${this._base}/super-admin/audience/buildings`, { params: httpParams })
      .pipe(map((res) => unwrapNewApiEnvelope<Paginate<AudienceBuilding>>(res)));
  }

  /** Repeats each array entry under the same query key — `HttpParams` requires `.append` per value. */
  private _audienceParams(audience: InAppCampaignAudience): HttpParams {
    let params = new HttpParams().set('scope', audience.scope);
    for (const id of audience.countryIds ?? []) params = params.append('countryIds', id);
    for (const id of audience.buildingCompanyIds ?? []) params = params.append('buildingCompanyIds', id);
    for (const id of audience.excludedBuildingIds ?? []) params = params.append('excludedBuildingIds', id);
    return params;
  }
}
